import { asIfAccessToken, touchAsIfView } from '@/features/as-if/asIfView';
import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { markSignedIn, markSignedOut } from './presenceCookie';
import { publishMaintenanceStatus } from '@/maintenance/maintenanceEvents';
import { isTermsRefusal, publishTermsRefusal } from '@/features/legal/termsRefusal';
import { maintenanceInterruptionFromHttpError } from '@/maintenance/maintenanceErrors';
import { effectiveDeploymentAddress } from '@/features/onboarding/deploymentAddress';
import { TOKEN_KEY, REFRESH_KEY, requestAccessToken } from './accessToken';
import { DesktopKeychainUnavailableError } from '@/desktop/desktopSessionErrors';
import {
  desktopSessionUnread,
  endDesktopSession,
  readStoredDesktopSession,
  writeDesktopSession,
  writeDesktopSessionDurably,
} from '@/desktop/desktopSessionLazy';
import { isDesktopRuntime } from '@/desktop/desktopDetection';
import { logSessionEnd } from '@/desktop/desktopSessionEndLog';
import { whileBusy } from '@/desktop/desktopBusy';
import { clearShellSession, writeShellSession } from '@/shell/shellSessionVault';
import { mirrorRefreshChain } from './nativeSessionMirror';
import { shellDeviceProof } from '@/shell/shellDeviceProof';
import {
  REFRESH_REFUSALS,
  parseCredentialKind,
  type CredentialKindType,
} from '@featit/shared/credentials';
import { requestIdText } from '@featit/shared/request-id';
import { FAILURE_REASONS, isGenericServerText } from './failureReasons';
import { failureReference } from './graphqlRequestError';
import {
  SESSION_END_LOCAL_REASONS,
  SESSION_END_PATHS,
  SESSION_END_REASONS,
  isStaleSecretRefusal,
  sessionEndReason,
  type SessionEndLogReasonType,
  type SessionEndPathType,
  type SessionEndReasonType,
} from './sessionEnd';
import { isPublicRoutePath } from '@/routing/publicRoutes';
import { assertOnlineForWrite } from '@/shell/shellOffline';
import { refreshDeviceHeaders } from './signInDevice';
import {
  MAX_PENDING_REFRESH_SECRETS,
  RefreshSecretUnreadableError,
  canWriteAhead,
  clearPendingRefreshSecrets,
  holdsCurrentRefreshSecret,
  newRefreshSecret,
  readCurrentRefreshSecret,
  readPendingRefreshSecrets,
  refreshSecretHash,
  removeCurrentRefreshSecret,
  restorePendingRefreshSecrets,
  writeCurrentRefreshSecret,
  writePendingRefreshSecrets,
} from './refreshSecretWriteAhead';
import { roleRefusalSentence } from './roleRefusalReason';
import { readerRefusalSentence } from './readerRefusalReason';
import { planRefusalSentence } from './planLimitRefusal';
import { replyDraftAgentRefusalSentence } from './replyDraftAgentRefusal';
import { verificationReadOnlySentence } from './verificationReadOnly';

export interface ApiServiceTransport {
  request<T>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    path: string,
    body?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<T>;
}

/**
 * Where a FEATit deployment mounts its API, under every deployment there has ever been.
 *
 * It used to be `VITE_API_PATH`, threaded through `Dockerfile.stable`, both compose files and
 * `stable-deploy.sh` — four places, all four passing `/api`. A build argument with one value is not
 * configuration, it is four opportunities to disagree, and one of them disagreeing would move the
 * whole API without the code that calls it knowing. The backend's own `MAIN_API_PATH` still exists
 * and still varies, because that one is read by a running server that can be reconfigured; this is
 * substituted into a bundle at compile time and can only ever be changed by a release.
 */
export const API_PATH = '/api';

export function buildApiBaseUrl(baseUrl?: string, apiPath: string = API_PATH): string {
  const normalizedBaseUrl = (baseUrl ?? '').trim().replace(/\/+$/, '');
  const normalizedApiPath = `/${apiPath.trim().replace(/^\/+|\/+$/g, '')}`;
  return `${normalizedBaseUrl}${normalizedApiPath}`;
}

/**
 * The deployment this app talks to, resolved once when the module loads.
 *
 * It stays a constant even though the address is now choosable, because the choice only takes
 * effect on a reload: `deploymentAddress.ts` writes it, the app restarts, and every module reads
 * the same answer. That is what keeps the seam one line wide — the alternative, a live-resolved
 * base URL, would leave every other holder of this constant (the realtime socket's endpoint, the
 * maintenance poll, the data-source registry) free to disagree with it mid-session, which is a
 * worse failure than a reload.
 */
export const API_BASE_URL = buildApiBaseUrl(effectiveDeploymentAddress());

/**
 * A request that never answers must still end.
 *
 * Without a deadline, an accepted connection that never replies — a proxy holding the socket open
 * across a container swap, a tunnel that dropped the return path — leaves the promise pending for
 * the life of the page. Nothing downstream can recover from that: a caller waiting on it never sees
 * a success to render or a failure to retry, and every guard built on "is a request in flight" stays
 * latched. This deadline is what turns a hang into an error, which is the only shape the rest of the
 * chain knows how to repair.
 */
export const DEFAULT_API_TIMEOUT_MS = 20_000;

export function readApiTimeoutMs(raw: unknown, fallback = DEFAULT_API_TIMEOUT_MS): number {
  const parsed = typeof raw === 'string' ? Number(raw.trim()) : Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

/*
 * Not configurable, and `readApiTimeoutMs` is kept because it is what the number is checked by.
 *
 * `VITE_API_TIMEOUT_MS` was read here and declared by no `ARG`, so it was already frozen to this
 * value in every image ever built — the read was decorative. The registry that recorded that state
 * said why it should stay one number: how long a reader will sit before giving up is a judgement
 * about people, not about a deployment's hardware, and a deployment that needed longer would be
 * telling us something about the backend that a bigger timeout would only hide.
 */
export const API_TIMEOUT_MS = readApiTimeoutMs(DEFAULT_API_TIMEOUT_MS);
/**
 * Credentials belong to the deployment that issued them, so the slots they live in are keyed by it.
 *
 * A single pair of global slots would have two deployments sharing one session: point the app
 * somewhere else and it presents a token minted by a tenant that address never heard of. Resolved
 * beside `API_BASE_URL` and from the same address, so the token in hand always belongs to the
 * deployment being addressed. An installation that has never chosen an address keeps the unscoped
 * slots it has always used, and is therefore not signed out by this change.
 */
export { TOKEN_KEY, REFRESH_KEY };

let refreshPromise: Promise<string> | null = null;

export function hasStoredAuthSession(): boolean {
  return (
    Boolean(localStorage.getItem(TOKEN_KEY)) ||
    holdsCurrentRefreshSecret(REFRESH_KEY) ||
    // I-2572 (MS-2): a keychain that did not answer may hold the session. The app checks it, and
    // says "Reconnecting…" while it cannot, rather than draw the sign-in.
    desktopSessionUnread()
  );
}

/**
 * The claims of the stored access token, read client-side — never a network round trip, and never
 * verified, because nothing here is authorised by it. `CredentialApi.ts`'s `currentCredentialId`
 * and `decodeStoredCredentialKind` below both used to carry their own copy of this base64url
 * decode; a tampered or absent token must fail the same way for both readers; a third caller with
 * its own copy is the failure this consolidation is for (see `credential-claims.ts` server-side,
 * which this UI-side reader deliberately does not duplicate the verification of).
 */
export function decodedAccessTokenClaims(): Record<string, unknown> | null {
  const payload = localStorage.getItem(TOKEN_KEY)?.split('.')[1];
  if (!payload) return null;
  try {
    // base64url, and `atob` reads base64: the alphabet differs in two characters and the padding
    // is dropped, both of which make an otherwise valid payload throw.
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '='))) as Record<
      string,
      unknown
    >;
  } catch {
    return null;
  }
}

/**
 * What kind of credential the stored access token claims to be (I-0353, AC-U) — read from the
 * token itself, so a write control can gate on it the instant it renders rather than after an
 * `AccessControlApi.me()` resolves.
 *
 * It is a UI convenience, not a security boundary — the boundary is `assertWorkAreaPermission`
 * and `RolesGuard` on the server, which run whether or not a button was disabled. `undefined` for
 * anything this cannot parse, which reads as "not a verification credential" — the same fail-open
 * a mis-shaped session token already gets from every other reader of it, so this does not carve
 * out a new failure mode.
 */
export function decodeStoredCredentialKind(): CredentialKindType | undefined {
  return parseCredentialKind(decodedAccessTokenClaims()?.kind);
}

/*
 * localStorage stays the working store, and the desktop keychain is kept alongside it.
 *
 * Not a migration, on purpose. Every reader of a token in this app is synchronous — `TOKEN_KEY` is
 * read inline in interceptors, in `WorkItemApi`, in `AuthStore`'s getters — and a keychain is an
 * IPC round trip. Making those readers async would spread this fix across the whole app to solve a
 * problem that only exists between processes, not within one.
 *
 * So: localStorage carries the session while the app runs, exactly as before, and the vault carries
 * it across restarts. `hydrateDesktopSession` in `index.tsx` puts the vault's copy back before the
 * app mounts, which is the only moment the two can disagree.
 *
 * The mirror is fire-and-forget. A keychain that cannot be written must not fail a sign-in that has
 * already succeeded — the cost of that failure is the behaviour we had before this existed.
 */
/** The desktop vault's end of the last session `clearAuthTokens` ended (never rejects). */
let desktopSessionEnd: Promise<void> = Promise.resolve();

/**
 * I-1637: resolves once the desktop vault has ended the session the page last ended. The session
 * refresher answers `ended` only after this (ADR-100's I-1637 amendment, point 2).
 */
export function desktopSessionEnded(): Promise<void> {
  return desktopSessionEnd;
}

export function clearAuthTokens(options: { keepDesktopSession?: boolean } = {}) {
  localStorage.removeItem(TOKEN_KEY);
  removeCurrentRefreshSecret(REFRESH_KEY);
  // I-1623: an unconfirmed successor belongs to the session that just ended.
  clearPendingRefreshSecrets(REFRESH_KEY);
  // ADR-133: the root shows the website again once nobody is signed in here.
  markSignedOut();
  // F-0534 PPD-3 (I-1247): every cause of a session's end reaches here — a sign-out, a refresh the
  // server refused, a session it no longer knows — so the desktop's own session-ended path runs
  // from here, not from the sign-out alone.
  // ⛔ I-2572 (MS-2): except an end this page derived by itself ("no secret here"). The keychain
  // may hold the very secret this page could not find, and deleting it is what made Jay's
  // sign-out unrecoverable. Only the person or the server ends the keychain's session.
  if (!options.keepDesktopSession) {
    desktopSessionEnd = endDesktopSession(effectiveDeploymentAddress());
  }
  // T-0320: the app's Keychain, by the same rule. Each vault answers only in its own runtime.
  void clearShellSession();
}

/**
 * F-0534 PPD-3 (I-1311): why a session ended, as far as the web can tell.
 *
 * `SERVER` is an expiry, a revocation and a refused refresh alike: the server answers them with one
 * refusal. `ELSEWHERE` is another tab or window of this origin, whose own cause this one cannot
 * see. `ANOTHER_PERSON` is a second person signed in on a page that still held the first.
 */
export const SESSION_END_CAUSES = {
  SIGN_OUT: 'sign-out',
  SERVER: 'server',
  ELSEWHERE: 'elsewhere',
  ANOTHER_PERSON: 'another person',
} as const;

export type SessionEndCauseType = (typeof SESSION_END_CAUSES)[keyof typeof SESSION_END_CAUSES];

/** The causes this page found for itself: the credentials are still here, and are cleared. */
const CAUSES_THAT_CLEAR_CREDENTIALS: readonly SessionEndCauseType[] = [
  SESSION_END_CAUSES.SIGN_OUT,
  SESSION_END_CAUSES.SERVER,
];

const sessionEndListeners = new Set<(cause: SessionEndCauseType) => void>();

/** Told of every session end, with its cause (`endSession`). Returns how to stop being told. */
export function onSessionEnd(listener: (cause: SessionEndCauseType) => void): () => void {
  sessionEndListeners.add(listener);
  return () => void sessionEndListeners.delete(listener);
}

/**
 * I-2572: where a session end came from and why, for the desktop log (MS-3), and whether the
 * desktop keychain keeps its copy (MS-2). Each cause has its default; a caller that knows more says so.
 */
export interface ISessionEndDetail {
  path?: SessionEndPathType;
  reason?: SessionEndLogReasonType;
  /** An end this page derived by itself, not one the server or the person decided. */
  keepDesktopSession?: boolean;
}

const DEFAULT_SESSION_END_DETAIL: Record<
  SessionEndCauseType,
  { path: SessionEndPathType; reason: SessionEndLogReasonType }
> = {
  [SESSION_END_CAUSES.SIGN_OUT]: {
    path: SESSION_END_PATHS.SIGN_OUT,
    reason: SESSION_END_LOCAL_REASONS.PERSON,
  },
  [SESSION_END_CAUSES.SERVER]: {
    path: SESSION_END_PATHS.SESSION_CHECK_REFUSED,
    reason: SESSION_END_LOCAL_REASONS.REFUSED,
  },
  [SESSION_END_CAUSES.ELSEWHERE]: {
    path: SESSION_END_PATHS.ENDED_ELSEWHERE,
    reason: SESSION_END_LOCAL_REASONS.STORAGE_CLEARED,
  },
  [SESSION_END_CAUSES.ANOTHER_PERSON]: {
    path: SESSION_END_PATHS.ANOTHER_PERSON,
    reason: SESSION_END_LOCAL_REASONS.PERSON_CHANGED,
  },
};

/**
 * F-0534 PPD-3 (I-1311): the one way a session ends in the web, whatever ended it. Clears the
 * credentials when this page is the one that found the end (elsewhere they are gone already, and
 * after a person change they are the next person's), then tells every listener the cause.
 *
 * ⛔ Nothing else calls `clearAuthTokens` (`oneSessionEnd.guard.test.ts`): a session ended past
 * this function leaves `authStore.user` set and tells no store, which is what a refused refresh did.
 *
 * I-2572 (MS-3): every end but the Sign out dialog's writes its path and reason to the desktop log,
 * never a secret. ⛔ *Mutation:* drop the `logSessionEnd` call → `ApiService.desktopSignOut.test.ts`
 * › MS-3 goes red: no line for a refused refresh.
 */
export function endSession(cause: SessionEndCauseType, detail: ISessionEndDetail = {}): void {
  const clears = CAUSES_THAT_CLEAR_CREDENTIALS.includes(cause);
  const keepsKeychain = !clears || detail.keepDesktopSession === true;
  const path = detail.path ?? DEFAULT_SESSION_END_DETAIL[cause].path;
  if (path !== SESSION_END_PATHS.SIGN_OUT_DIALOG) {
    logSessionEnd({
      path,
      reason: detail.reason ?? DEFAULT_SESSION_END_DETAIL[cause].reason,
      cause,
      keychain: keepsKeychain ? 'kept' : 'cleared',
    });
  }
  if (clears) clearAuthTokens({ keepDesktopSession: detail.keepDesktopSession });
  for (const listener of [...sessionEndListeners]) {
    try {
      listener(cause);
    } catch (error) {
      // One listener failing must not leave the others holding the ended person.
      console.warn('[featit] session end: a listener failed', error);
    }
  }
}

/**
 * Store a session's tokens. The access token is written at once; the refresh secret is wrapped
 * first (I-1625), so it is stored when the returned promise resolves. Never rejects.
 *
 * ⛔ **A caller inside the refresh lock awaits this** (`exchangeRefreshSecret`): the next tab to take
 * the lock must read the secret this refresh rotated to, never the one it retired (ADR-100 I-1625
 * §5). A sign-in may leave it running: `hasStoredAuthSession` counts a secret still being wrapped.
 */
export async function storeAuthTokens(tokens: {
  accessToken: string;
  refreshToken?: string;
}): Promise<void> {
  localStorage.setItem(TOKEN_KEY, tokens.accessToken);
  // ADR-133: signed in here, so the root shows the app. Every sign-in path and every refresh stores
  // its tokens through this function, so none can leave the cookie behind.
  markSignedIn();
  let refreshToken: string | null = tokens.refreshToken ?? null;
  if (tokens.refreshToken) {
    await writeCurrentRefreshSecret(REFRESH_KEY, tokens.refreshToken);
    // I-1623: a secret handed to us is a new current one — a sign-in, or a minted rotation — and
    // no candidate chosen for the one before it can follow it. Cleared only once the new current
    // one is stored, so a write-ahead successor is never in neither slot.
    clearPendingRefreshSecrets(REFRESH_KEY);
  } else {
    /*
     * The refresh token is read back from storage rather than left out.
     *
     * A refresh response carries a new access token and often no refresh token, and the existing
     * one stays valid — mirroring only `tokens.refreshToken` would drop it from the vault on every
     * refresh, so the session would survive a restart only until the first token refresh. That is a
     * bug that would look like an intermittent fix.
     */
    refreshToken = await readCurrentRefreshSecret(REFRESH_KEY).catch(() => null);
  }
  // The native vaults get the plain secret: the operating system encrypts them (I-1625 §7).
  // T-0666: a new current secret clears the vault's candidates in the same write, as it cleared the
  // webview's above; without one the field is left out, and the vault keeps what it holds.
  const mirrored = {
    accessToken: tokens.accessToken,
    refreshToken,
    ...(tokens.refreshToken ? { pendingRefreshTokens: [] } : {}),
  };
  void writeDesktopSession(effectiveDeploymentAddress(), mirrored);
  void writeShellSession(mirrored);
}

/**
 * Whether the server refused the request's credential: a 401, and only a 401. ⛔ A 403 is "not
 * allowed", said to a reader whose session is fine (a guest asking for a member's read, AGM-41), so
 * it is never refreshed and never signs anyone out. `signOutOn401Only.guard.test.ts` pins this.
 */
export function isHttpAuthError(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return status === 401;
}

/**
 * The request hit its deadline rather than being refused.
 *
 * Worth telling apart from every other transport failure because it is the one where nothing is
 * known to be wrong: the server may be fine and merely slow, or gone entirely. What can be said is
 * only that no answer came, which is a different sentence from "the request failed".
 */
export function isRequestTimeoutError(error: unknown): boolean {
  const candidate = error as { code?: unknown; response?: unknown } | null | undefined;
  if (candidate?.response) return false;
  return candidate?.code === 'ECONNABORTED' || candidate?.code === 'ETIMEDOUT';
}

/**
 * I-1829 (EM-20): the sentence for a request that got no answer. A request that ran out its deadline
 * while the device is online took too long. It was sent, and the deployment may be answering every
 * other request, so "could not be reached" would be untrue. Only a request that never got through,
 * or any request while the device is offline, says the deployment could not be reached.
 */
export function noAnswerReason(
  error: unknown,
  reasons: HttpFailureReasonsType = FAILURE_REASONS,
): string {
  const deviceOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
  return isRequestTimeoutError(error) && !deviceOffline ? reasons.stalled : reasons.offline;
}

/**
 * The reasons `httpErrorMessage` says after its sentence: English (`FAILURE_REASONS`) unless a
 * screen hands it the reader's (`httpErrorMessageIn`, F-0621). This file stays free of the catalogue.
 */
export type HttpFailureReasonsType = Readonly<Record<'offline' | 'stalled' | 'server', string>>;

export function isTransientHttpError(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (!status) return true;
  return status >= 500;
}

/**
 * What the server said, not what the transport said about the request.
 *
 * The distinction that matters is transport versus domain, not Error versus not. A transport
 * rejection's own `message` is written for a network log — "Request failed with status code 400"
 * for every 400 alike, "Network Error" for every dropped request alike — so reporting it replaces a
 * sentence meant for the reader with one that explains nothing. The real one is in the response
 * body, as a string or, from the validation pipe, as a list.
 *
 * A plain `Error` someone threw deliberately is the opposite case: its message *is* the sentence
 * written for the reader, so it is kept.
 */
export function httpErrorMessage(
  error: unknown,
  fallback: string,
  reasons: HttpFailureReasonsType = FAILURE_REASONS,
): string {
  // I-0935 EM-4, I-1470 EM-9: the server's request id for the failure after the sentence, as
  // `actionFailureText` ends its line, so a screenshot places the failure in the server's log.
  const reference = failureReference(error);
  const sentence = httpErrorSentence(error, fallback, reasons);
  return reference ? `${sentence.replace(/\s+$/, '')} ${requestIdText(reference)}.` : sentence;
}

function httpErrorSentence(
  error: unknown,
  fallback: string,
  reasons: HttpFailureReasonsType,
): string {
  // I-1712 (AR-7.12): in the iOS app a plan's refusal is said from its code, with no plan to buy.
  const planRefusal = planRefusalSentence(error);
  if (planRefusal) return planRefusal;
  // I-2195: a refusal that names a role is said from its code and the role's facts, in the page's language.
  const roleRefusal = roleRefusalSentence(error);
  if (roleRefusal) return roleRefusal;
  const readOnly = verificationReadOnlySentence(error);
  if (readOnly) return readOnly;
  // I-2439: a refusal the server named by its code, in the page's language.
  const coded = readerRefusalSentence(error);
  if (coded) return coded;
  const draftAgent = replyDraftAgentRefusalSentence(error);
  if (draftAgent) return draftAgent;
  const candidate = error as
    | {
        isAxiosError?: boolean;
        request?: unknown;
        response?: { status?: unknown; data?: { message?: unknown } };
        message?: unknown;
        extensions?: { code?: unknown; originalError?: { statusCode?: unknown } };
      }
    | null
    | undefined;
  const transport = Boolean(candidate?.isAxiosError || candidate?.response || candidate?.request);
  if (transport) {
    const status = candidate?.response?.status;
    // I-0398: a 5xx body is Nest's "Internal server error", and a request with no response has no
    // body at all. Neither is a reason; both get what happened and what to do. A request with no
    // response either timed out or never got through, and the two say different things (I-1829).
    if (!candidate?.response) return `${fallback} ${noAnswerReason(error, reasons)}`;
    if (typeof status === 'number' && status >= 500) return `${fallback} ${reasons.server}`;
    const message = candidate.response.data?.message;
    if (typeof message === 'string' && message.trim() && !isGenericServerText(message)) {
      return message;
    }
    if (Array.isArray(message)) {
      const sentences = message.filter((entry): entry is string => typeof entry === 'string');
      if (sentences.length > 0) return sentences.join(' ');
    }
    return fallback;
  }
  // A GraphQL rejection keeps its code: an internal error's text is not a reason either.
  const code = candidate?.extensions?.code;
  const graphqlStatus = candidate?.extensions?.originalError?.statusCode;
  if (
    code === 'INTERNAL_SERVER_ERROR' ||
    (typeof graphqlStatus === 'number' && graphqlStatus >= 500)
  ) {
    return `${fallback} ${reasons.server}`;
  }
  return typeof candidate?.message === 'string' &&
    candidate.message.trim() &&
    !isGenericServerText(candidate.message)
    ? candidate.message
    : fallback;
}

/**
 * The requests a 401 is the *answer* to, rather than a symptom of an expired access token.
 *
 * Signing in, registering and refreshing are how a session is established; a 401 from one of them
 * means the credentials were refused, and no amount of refreshing repairs that. Running the retry
 * over them costs twice. The rejection the caller finally sees is the refresh's and not the
 * server's, so a wrong password reported *"Invalid email or password"* arrived at the sign-in form
 * as **"No refresh token available."** — an internal sentence, shown to a reader, in place of the
 * one written for them. And when a stale refresh token happens to be in storage the refresh 401s
 * in turn, which sends the branch below into `window.location.href` — a **full page reload in the
 * middle of a sign-in submit**, which is also why the browser never offered to save the password:
 * a password manager stores on a submit it can follow, and this navigated away from it.
 */
const SESSION_ESTABLISHING_PATHS = [
  '/auth/login',
  '/auth/register',
  '/auth/refresh',
  '/auth/passkeys/authentication/options',
  '/auth/passkeys/authentication/verify',
  /**
   * I-0326. `VerifySessionPage` presents a one-time verification code, not a bearer token — there
   * is no session yet to refresh, exactly the `/auth/refresh` reasoning above. Without this, a
   * spent or expired code's 401 triggered a refresh (which itself 401s, having no token to
   * refresh), and the branch above sent the reader to `/login` via a full page reload before
   * `VerifySessionPage`'s own catch ever got to show "this link no longer works" — the same
   * "internal sentence in place of the one written for them" failure this list already exists to
   * prevent, landing on the one auth surface that has no session to lose.
   */
  '/auth/verify-session/redeem',
  /**
   * F-0160. `VerifyEmailPage` and `ResetPasswordPage` present a one-time token in the request body,
   * not a bearer token — the same "no session yet to refresh" shape as `/auth/verify-session/redeem`
   * above. Without this, an expired or already-used link's 401 sent the reader to `/login` via a
   * full-page reload before either page's own catch could show "this link is invalid or has
   * expired" — observed directly: `page.goto('/verify-email?token=…')` landed on `/login` instead.
   */
  '/auth/email-verification/confirm',
  '/auth/password-reset/confirm',
  /**
   * T-0633 (F-0563 D7). The reset's last step, from the person who just finished it: no session, a
   * review grant in the body, and a 401 that means "this review has ended", written for them.
   */
  '/auth/password-reset/review/remove',
  /**
   * F-0557. The two links of a sign-in address change present a one-time token in the body from
   * whoever opened the mailbox, signed in or not — the same shape as the two confirm doors above.
   */
  '/auth/sign-in-address/confirm',
  '/auth/sign-in-address/undo',
  /**
   * F-0160 AC-25b. Presents an email and password, exactly like `/auth/login`, from a person who
   * has no session — a 401 here is "wrong password", and refreshing cannot repair it.
   */
  '/auth/email-verification/resend-for-sign-in',
  /**
   * F-0160 §11 (I-0638). `SetupPage` presents a one-time setup token in the body, from a person who
   * has no account at all yet. A 401 here is "this token is spent" or "this is not that account's
   * password", in a sentence written for the operator — observed: without this entry the 401 sent
   * the operator to `/login` before the setup page could show it.
   */
  '/auth/setup',
  /**
   * F-0160 §11.5 (I-0638). The browser door and the claim link: a person with no account, whose
   * 401 or 403 is "the window has closed" or "this link is spent", written for them.
   */
  '/auth/setup/first-run',
  '/auth/setup/passkey-options',
  '/auth/setup/claim/peek',
  '/auth/setup/claim',
];

export function establishesSession(url: string | undefined): boolean {
  if (!url) return false;
  // Compared against the path alone: callers pass a relative URL, but an absolute one reaches here
  // too, and `endsWith` on the raw string would also match a query string onto the end of it.
  const path = url.split('?')[0].replace(/\/+$/, '');
  return SESSION_ESTABLISHING_PATHS.some((candidate) => path.endsWith(candidate));
}

/**
 * A session that ended, told apart from a refresh the server refused.
 *
 * Both mean the same thing to a reader — sign in again — and neither is a sentence to show them,
 * so this exists to be recognised rather than read.
 */
/**
 * Where to send a reader whose session just ended — and when to leave them where they are.
 *
 * A decision rather than a navigation, so it can be checked: the navigation itself cannot be
 * observed under jsdom, and a test asserting the address afterwards passes whether the guard is
 * there or not. Widening the end-of-session branch to cover an *absent* refresh token brought
 * `/login` into its reach, where a redirect is a navigation to the page doing the navigating —
 * a reload loop that wipes a half-typed password each time round.
 */
export function loginRedirectTarget(
  pathname: string,
  reason?: SessionEndReasonType | null,
): string | null {
  // I-0942: `null` on every public route (`isPublicRoutePath`) — a reporter's page, an invitation,
  // any door before an account. Those never had a session to end, so a 401 there is answered on the
  // page itself, never by a hard navigation away that would drop what the reader had open.
  if (isPublicRoutePath(pathname)) return null;
  // ⭐ A code, never a sentence (I-0464). `LoginPage` turns it into words it owns, so a reader who
  // was signed out mid-sentence is told why instead of finding themselves at a sign-in form.
  return reason ? `/login?sessionEnded=${encodeURIComponent(reason)}` : '/login';
}

export class SessionExpiredError extends Error {
  /**
   * I-2572: `server` when the server refused every secret this page could present; `local` when this
   * page found nothing it could present. Only a `server` end clears the desktop keychain, and a
   * `local` one whose keychain copy holds no refresh secret either (`keychainHoldsNoSecret`, I-2617).
   */
  readonly origin: 'server' | 'local';
  /**
   * I-2617 (RES-15b): the desktop keychain was read and its copy holds an access token without a
   * refresh secret. Nothing can renew it, so keeping it only restores a dead session at every launch.
   */
  readonly keychainHoldsNoSecret: boolean;

  constructor(origin: 'server' | 'local', options: { keychainHoldsNoSecret?: boolean } = {}) {
    super('Your session has ended. Please sign in again.');
    this.name = 'SessionExpiredError';
    this.origin = origin;
    this.keychainHoldsNoSecret = options.keychainHoldsNoSecret === true;
  }
}

/**
 * Read a refresh slot for a refresh. A value that will not unwrap — the key was cleared or
 * replaced, the value damaged — is a session that is over (ADR-100 I-1625 §4): it ends with the
 * stale reason, the slots are cleared, and the page signs in again rather than loop or crash.
 *
 * ⛔ *Mutation:* let `RefreshSecretUnreadableError` through → `ApiService.vault.test.ts` goes red: no
 * stale reason and no redirect to the sign-in.
 */
async function readForRefresh<T>(read: () => Promise<T>): Promise<T> {
  try {
    return await read();
  } catch (error) {
    if (error instanceof RefreshSecretUnreadableError) throw new SessionExpiredError('local');
    throw error;
  }
}

/**
 * The Web Lock every tab of this origin refreshes under (I-0464).
 *
 * Named for the app rather than for the deployment on purpose: locks are scoped to an origin
 * already, and two deployments addressed from one origin share a `localStorage` too, so they should
 * queue behind each other rather than race.
 */
export const REFRESH_LOCK_NAME = 'featit-refresh';

interface ILockManager {
  request<T>(name: string, run: () => Promise<T>): Promise<T>;
}

/**
 * One refresh at a time per origin — or, where Web Locks is missing, straight through.
 *
 * ⛔ **The fallback is not a degraded mode.** With the server half in place (ADR-100) a second tab
 * that refreshes anyway is answered rather than refused, so the lock buys a saved round trip and a
 * tidier server log, not correctness. Making the fallback throw, or gating the refresh on Web Locks
 * being present, would turn a missing convenience into a sign-out.
 */
async function underRefreshLock<T>(run: (lockHeld: boolean) => Promise<T>): Promise<T> {
  const locks = (navigator as Navigator & { locks?: ILockManager }).locks;
  if (typeof locks?.request !== 'function') return run(false);
  let entered = false;
  try {
    return await locks.request(REFRESH_LOCK_NAME, () => {
      entered = true;
      return run(true);
    });
  } catch (error) {
    // A lock manager that refuses to hand out the lock at all (an insecure context, a shim that
    // throws) must not be the reason somebody is signed out. A failure from inside the lock is the
    // refresh's own, and is not run a second time without it.
    if (entered) throw error;
    if (error instanceof SessionExpiredError || isHttpAuthError(error)) throw error;
    if (error instanceof DOMException) return run(false);
    throw error;
  }
}

/** Whether a refused refresh asks for the device key's signature (F-0332). */
function asksForDeviceProof(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: { code?: unknown } } })
    ?.response;
  return response?.status === 401 && response.data?.code === REFRESH_REFUSALS.DEVICE_PROOF_REQUIRED;
}

/**
 * The shell's signature over a fresh nonce, for a session paired by QR code (F-0332), or null when
 * this page cannot make one — not in the app, or no key for this deployment.
 */
async function deviceProof(): Promise<{ nonce: string; signature: string } | null> {
  const issued = await axios.post<{ nonce: string }>(`${API_BASE_URL}/auth/refresh/nonce`, null, {
    timeout: API_TIMEOUT_MS,
  });
  const nonce = issued.data?.nonce;
  if (typeof nonce !== 'string' || !nonce) return null;
  const signature = await shellDeviceProof(nonce);
  return signature ? { nonce, signature } : null;
}

/**
 * Present `secret`. With `writeAhead` this is a write-ahead rotation (I-1623): the server rotates to
 * the hash named and answers an access token only, and the successor behind it becomes the current
 * secret. Without it the server mints the successor and sends it back, as it always has.
 */
async function exchangeRefreshSecret(
  secret: string,
  writeAhead?: { successor: string; nextSecretHash: string },
): Promise<string> {
  const nextSecretHash = writeAhead?.nextSecretHash;
  const post = async (proof: { deviceProof: { nonce: string; signature: string } } | null) => {
    // Kept `null` for a minted rotation with no proof, exactly the request it has always been.
    const body = nextSecretHash === undefined ? proof : { nextSecretHash, ...(proof ?? {}) };
    return axios.post<{ accessToken: string; refreshToken?: string }>(
      `${API_BASE_URL}/auth/refresh`,
      body,
      {
        // I-1034 AC-6: in the Mac app, the interface address it is on now.
        headers: { Authorization: `Bearer ${secret}`, ...(await refreshDeviceHeaders()) },
        // This one bypasses the instance above, so it needs the deadline stated again. A refresh
        // that hangs holds `refreshPromise`, and every 401 behind it waits on the same hang.
        timeout: API_TIMEOUT_MS,
      },
    );
  };
  let res: Awaited<ReturnType<typeof post>>;
  try {
    res = await post(null);
  } catch (error) {
    // F-0332: a session the app paired by QR code renews only with its device key's signature. The
    // refusal changed nothing on the server, so asking once more, signed, is safe.
    if (!asksForDeviceProof(error)) throw error;
    const proof = await deviceProof();
    if (!proof) throw error;
    // The shell signs `pairedRefreshProofMessage(nonce)` from `@featit/shared`, the bytes the
    // server verifies.
    res = await post({ deviceProof: proof });
  }
  // ⭐ Only what came back is written. A replayed refresh (ADR-100) carries an access token and no
  // secret, and `storeAuthTokens` leaves the stored secret alone — which is precisely the winner's
  // fresh one. Writing this holder's stale secret back would undo the rotation it just lost.
  // A deployment from before ADR-096 ignores the hash and mints a secret: what it sent back wins,
  // as the bridge's `settle` does. Otherwise the chosen successor is confirmed as current, and
  // `storeAuthTokens` clears the candidates it was one of. Awaited, inside the lock (I-1625 §5).
  await storeAuthTokens(
    writeAhead && !res.data.refreshToken
      ? { accessToken: res.data.accessToken, refreshToken: writeAhead.successor }
      : res.data,
  );
  return res.data.accessToken;
}

/**
 * I-2572 (MS-1): the desktop's refresh chain from the keychain, for a page that holds no secret of
 * its own — an older build's `vault1.` slot it cannot unwrap (D-0315 a), or a hydration that never
 * ran. Put back into the page's slots, candidates with it, and returned; `null` when the keychain
 * holds none. A keychain that does not answer, after its retries, throws
 * `DesktopKeychainUnavailableError`: that is no session end, and nobody is signed out for it (MS-2).
 *
 * ⛔ *Mutation:* return `null` here → `ApiService.desktopSignOut.test.ts` › "renews from the keychain"
 * goes red: `SessionExpiredError`, and the page goes to `/login`.
 */
async function secretFromDesktopKeychain(): Promise<string | null> {
  if (!isDesktopRuntime()) return null;
  const read = await readStoredDesktopSession(effectiveDeploymentAddress());
  if (read.state === 'unavailable') throw new DesktopKeychainUnavailableError();
  if (read.state === 'none') return null;
  // I-2617 (RES-15b): a copy with no refresh secret is a session nothing can renew; the end clears it.
  if (!read.session.refreshToken)
    throw new SessionExpiredError('local', { keychainHoldsNoSecret: true });
  const secret = read.session.refreshToken;
  await writeCurrentRefreshSecret(REFRESH_KEY, secret);
  await restorePendingRefreshSecrets(REFRESH_KEY, read.session.pendingRefreshTokens);
  return secret;
}

/**
 * Refresh once, inside the lock — the double-checked half of I-0464.
 *
 * @param tokenInHand the access token this holder set out to replace, read before it queued for the
 * lock. If the store no longer holds it, another tab refreshed while this one waited, and the right
 * answer is already in front of us.
 */
async function refreshUnderLock(tokenInHand: string | null, lockHeld: boolean): Promise<string> {
  const stored = localStorage.getItem(TOKEN_KEY);
  if (stored && stored !== tokenInHand) return stored;

  const secret =
    (await readForRefresh(() => readCurrentRefreshSecret(REFRESH_KEY))) ??
    (await secretFromDesktopKeychain());
  // Nothing to refresh with is the same state as a refresh the server refused: the session is
  // over. It used to reject with a plain `Error`, which `httpErrorMessage` reads as a sentence
  // someone wrote deliberately for a reader — so the internal note became the visible error.
  // I-2572: but this page found it, not the server, so the desktop keychain keeps its copy.
  if (!secret) throw new SessionExpiredError('local');

  try {
    // ⛔ Write-ahead only while this tab holds the lock (I-1623). The candidates are one store
    // shared by every tab, and a write-ahead rotation has no replay window: two tabs choosing
    // candidates at once would each overwrite the other's. Without the lock the minted rotation and
    // ADR-100's replay carry the race, exactly as before.
    if (lockHeld && canWriteAhead()) {
      const settled = await rotateWriteAhead(secret);
      if (settled !== null) return settled;
    }
    return await exchangeRefreshSecret(secret);
  } catch (error) {
    // The server named the session over, or the request never got an answer. Neither is repaired by
    // presenting a different secret.
    if (!isStaleSecretRefusal(error)) throw error;

    const current = await readForRefresh(() => readCurrentRefreshSecret(REFRESH_KEY));
    // ⭐ Another holder rotated past our copy and wrote the successor where we can read it. One
    // retry, with what the store says now.
    if (current && current !== secret) return exchangeRefreshSecret(current);

    /*
     * The secret the store itself holds is the one the server does not know, so there is nothing
     * better here and no other holder to protect: this genuinely is the end of the session.
     *
     * ⛔ Told apart from the case above by re-reading, never assumed. Under the lock the holder that
     * won released it only after writing, so "the store still holds what I presented" cannot be a
     * race we are looking at too early.
     */
    throw new SessionExpiredError('server');
  }
}

/**
 * One write-ahead rotation, recovering any rotation whose answer was lost (I-1623, ADR-096 §3).
 *
 * The chain is the current secret followed by the candidates, oldest first; each candidate is the
 * successor named for the one before it. The newest is presented first, with a fresh successor
 * stored before the request leaves. A secret the server never saw is refused without consequence,
 * so it is dropped and the one before it is presented, still naming it as its successor. Only once
 * every newer one has been refused is an older one presented — which is what proves the server
 * never rotated past it.
 *
 * Returns the new access token, or `null` when the store would not keep a candidate: nothing naming
 * it was sent, and the caller falls back to a minted rotation.
 *
 * ⛔ *Mutation:* present the current secret before the candidates → a lost answer revokes the session
 * (`ApiService.writeAhead.test.ts`).
 */
async function rotateWriteAhead(current: string): Promise<string | null> {
  const chain = [current, ...(await readForRefresh(() => readPendingRefreshSecrets(REFRESH_KEY)))];
  // Each candidate past the first is one more answer lost in a row. Past this many, the store is not
  // one any rotation can be trusted to: the session ends and is signed in again.
  if (chain.length > MAX_PENDING_REFRESH_SECRETS) throw new SessionExpiredError('local');
  for (let index = chain.length - 1; index >= 0; index -= 1) {
    if (index === chain.length - 1) {
      chain.push(newRefreshSecret());
      try {
        // ⛔ Awaited: the candidate is wrapped and stored before the request that names it is built
        // (ADR-100 I-1625 §5, VA-4).
        await writePendingRefreshSecrets(REFRESH_KEY, chain.slice(1));
        // T-0666: and the whole chain is in the native vault, in the desktop and the iOS shell,
        // whose webview storage a reset can empty. A vault that refuses rejects like the store.
        await mirrorRefreshChain({
          accessToken: localStorage.getItem(TOKEN_KEY) ?? '',
          refreshToken: chain[0],
          pendingRefreshTokens: chain.slice(1),
        });
      } catch (error) {
        // ADR-096 §1: a secret that could not be stored is never named in a request. With nothing
        // unconfirmed the minted rotation is still open; with candidates held, nothing is sent.
        if (index === 0) return null;
        throw error;
      }
    }
    const presented = chain[index];
    const successor = chain[index + 1];
    try {
      return await exchangeRefreshSecret(presented, {
        successor,
        nextSecretHash: await refreshSecretHash(successor),
      });
    } catch (error) {
      // A refusal that is not "never saw it" — the session is over, a reuse, a rate limit, or no
      // answer at all — is not repaired by presenting an older secret, and the candidates stay:
      // after no answer the server may well hold the newest of them.
      if (!isStaleSecretRefusal(error) || index === 0) throw error;
      // Never seen, so never current: what came after it can never become current either.
      chain.splice(index + 1);
      await writePendingRefreshSecrets(REFRESH_KEY, chain.slice(1));
    }
  }
  // Every candidate, and the current secret, was refused as unknown.
  throw new SessionExpiredError('server');
}

/**
 * F-0534 PPD-3 (I-1311): a refresh failed. When that means the session is over — the server said so,
 * or nothing in the store can be refreshed any more — the session ends through `endSession`, and
 * the page goes to the sign-in with the reason. A refresh that failed for another cause (the
 * network, a stale copy another holder has since replaced) ends nothing.
 *
 * `hadCredential` is read before the refresh: somebody who never had a session gets the sign-in
 * page without a sentence about one. Used by every caller of `refreshAccessToken` that acts on a
 * refused request: the REST interceptor and `GraphqlClient`.
 */
export function endSessionTheServerRefused(refreshError: unknown, hadCredential: boolean): boolean {
  const serverReason = sessionEndReason(refreshError);
  const reason =
    serverReason ??
    (refreshError instanceof SessionExpiredError ? SESSION_END_REASONS.STALE : null);
  // I-1637: says whether the session ended, so the desktop's session refresher answers `ended`.
  if (!reason) return false;
  // I-2572 (MS-2): only a refusal from the server ends the keychain's copy. "No secret here" is this
  // page's own finding, and the keychain may hold the secret it could not find. ⛔ *Mutation:* drop
  // `keepDesktopSession` → `ApiService.desktopSignOut.test.ts` › MS-2 goes red: `end_desktop_session`.
  const local =
    serverReason === null &&
    refreshError instanceof SessionExpiredError &&
    refreshError.origin === 'local';
  // I-2617 (RES-15b): unless the keychain was read and holds no secret either: that copy can never
  // renew, and kept, the next launch restored it and ended it again. ⛔ *Mutation:* keep it →
  // `ApiService.desktopSignOut.test.ts` › RES-15b "local end, empty keychain" red: no `end_desktop_session`.
  const keepsKeychain =
    local && !(refreshError instanceof SessionExpiredError && refreshError.keychainHoldsNoSecret);
  // Read first: once the stores are told, the app may already be on its way elsewhere.
  const target = loginRedirectTarget(window.location.pathname, hadCredential ? reason : null);
  endSession(SESSION_END_CAUSES.SERVER, {
    path: SESSION_END_PATHS.REFRESH_REFUSED,
    reason: local ? SESSION_END_LOCAL_REASONS.NO_SECRET : reason,
    keepDesktopSession: keepsKeychain,
  });
  if (target) window.location.href = target;
  return true;
}

export async function refreshAccessToken(): Promise<string> {
  if (refreshPromise) return refreshPromise;

  // Read before queueing for the lock, so that what we compare against inside it is the token this
  // refresh was asked for and not one some other tab has since written.
  const tokenInHand = localStorage.getItem(TOKEN_KEY);
  refreshPromise = underRefreshLock((lockHeld) => refreshUnderLock(tokenInHand, lockHeld)).finally(
    () => {
      refreshPromise = null;
    },
  );

  return refreshPromise;
}

/**
 * I-1637 (ADR-100's I-1637 amendment): run `run` inside the `featit-refresh` lock, handing it the
 * page's own refresh of the token the store holds at that moment — write-ahead and mirrored to the
 * native vault (T-0666), exactly as a refused request of the page's would renew.
 *
 * For the desktop's session refresher, which must compare the refused token with the one held and
 * write the vault while no tab can rotate. ⛔ `renew` is called only from inside `run`: Web Locks are
 * not re-entrant, so `refreshAccessToken` from there would wait on itself.
 */
export function renewUnderRefreshLock<T>(
  run: (renew: () => Promise<string>) => Promise<T>,
): Promise<T> {
  return underRefreshLock((lockHeld) =>
    run(() => refreshUnderLock(localStorage.getItem(TOKEN_KEY), lockHeld)),
  );
}

/**
 * I-1637: write the session this page holds — its access token and its current secret — to the
 * desktop vault, awaited, with the candidates left as the vault holds them. `false` when there is
 * no session to write or the vault would not keep it. Called inside the refresh lock, so the pair is
 * one no tab has rotated past.
 */
export async function mirrorHeldSessionToDesktop(): Promise<boolean> {
  const accessToken = localStorage.getItem(TOKEN_KEY);
  const refreshToken = await readForRefresh(() => readCurrentRefreshSecret(REFRESH_KEY));
  if (!accessToken || !refreshToken) return false;
  return writeDesktopSessionDurably(effectiveDeploymentAddress(), { accessToken, refreshToken });
}

/**
 * A refresh for a caller outside the REST interceptor (`GraphqlClient`): the new token, or, when
 * the refresh is refused because the session is over, the session ended like every other and the
 * refusal thrown on.
 */
export async function refreshOrEndSession(): Promise<string> {
  const hadCredential = holdsCurrentRefreshSecret(REFRESH_KEY);
  try {
    return await refreshAccessToken();
  } catch (refreshError) {
    endSessionTheServerRefused(refreshError, hadCredential);
    throw refreshError;
  }
}

/**
 * The person's own state has no view (D3): under a view a read of it is refused, and the page keeps
 * going on what it has. Two reads are the exception, the session's own check and its refresh.
 */

/**
 * The person's session upkeep goes as the person, whatever tab sends it. A view has no refresh secret and
 * is never refreshed (its expiry slides on use); the refresh the page makes is the person's own, and a
 * refusal of it must not be a view's to take (or to end) either way.
 */
function isTheSessionsOwnUpkeep(url: string | undefined): boolean {
  return pathOfRequest(url) === 'auth/refresh';
}

function pathOfRequest(url: string | undefined): string {
  const raw = (url ?? '').split('?')[0];
  const path = /^https?:\/\//.test(raw) ? new URL(raw).pathname.replace(/^\/api\//, '') : raw;
  return path.replace(/^\/+/, '').replace(/\/+$/, '');
}

/**
 * What a tab that holds a view needs to know of a request (`features/as-if/asIfEnding.ts`): it is the
 * person's own, and what the server would answer without asking it. A chunk of its own, loaded by the
 * boot of such a tab and handed in here, so the launch entry carries none of it.
 */
interface IAsIfRequestRules {
  isPersonalToAView(config: AxiosRequestConfig): boolean;
  skippedForTheView(config: AxiosRequestConfig): Promise<never>;
}
let asIfRequestRules: IAsIfRequestRules | null = null;

export function registerAsIfRequestRules(rules: IAsIfRequestRules): void {
  asIfRequestRules = rules;
}

export { pathOfRequest };

export class ApiService {
  protected client: AxiosInstance;

  constructor(private readonly transport?: ApiServiceTransport) {
    this.client = axios.create({ baseURL: API_BASE_URL, timeout: API_TIMEOUT_MS });
    if (transport) return;

    // I-1380: synchronous, so a request is sent in the call that makes it rather than a few
    // microtasks later. The boot sends the session check before it asks for the launch's code, and
    // that order only holds if the check is out by the time the call returns.
    this.client.interceptors.request.use(
      (config) => {
        // F-0709: a tab holding an as-if view speaks with the view's own credential, never the person's.
        const viewToken = isTheSessionsOwnUpkeep(config.url) ? null : asIfAccessToken();
        const token = viewToken ?? localStorage.getItem(TOKEN_KEY);
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        if (viewToken) {
          touchAsIfView();
          // The person's own state is never asked of the server as the view: it would only be
          // refused, and a refusal is written to the trail.
          const rules = asIfRequestRules;
          if (rules?.isPersonalToAView(config))
            config.adapter = () => rules.skippedForTheView(config);
        }
        return config;
      },
      undefined,
      { synchronous: true },
    );

    this.client.interceptors.response.use(
      (res) => res,
      async (error) => {
        const maintenanceInterruption = maintenanceInterruptionFromHttpError(error);
        if (maintenanceInterruption) {
          publishMaintenanceStatus(maintenanceInterruption.maintenanceStatus);
          return Promise.reject(maintenanceInterruption);
        }
        const original = error.config as AxiosRequestConfig & {
          _retry?: boolean;
        };
        // F-0709: the view has no refresh secret. A 401 is its end (it expired, was stopped, or the
        // work area's setting went off); it never refreshes the person's own session. A refusal at a
        // place a view cannot go is said by a page, not by the screen that asked.
        if (asIfAccessToken() && !isTheSessionsOwnUpkeep(original?.url)) {
          // The as-if rules of a refusal are the view's chunk (`asIfEnding.ts`), loaded by the boot of this tab.
          return (await import('@/features/as-if/asIfEnding')).refusedUnderView(error, original);
        }
        // I-2273 (AC-TA19): refused for the terms in force: the terms screen opens; nobody is signed out.
        if (isTermsRefusal(error)) publishTermsRefusal();
        if (isHttpAuthError(error) && !original._retry && !establishesSession(original.url)) {
          original._retry = true;
          // Read before the refresh, so a session that was never there is told apart from one that
          // ended: somebody who simply is not signed in gets the sign-in page without a sentence
          // explaining a session they never had.
          const hadCredential = holdsCurrentRefreshSecret(REFRESH_KEY);
          try {
            const newToken = await refreshAccessToken();
            original.headers = {
              ...original.headers,
              Authorization: `Bearer ${newToken}`,
            };
            return this.client(original);
          } catch (refreshError) {
            /*
             * ⛔ **Clearing is for a session the server says is over — never for "my copy went
             * stale" (I-0464).** `endSession` empties the `localStorage` every tab of this
             * origin shares, so a tab that cleared on a stale-secret 401 signed out the tab that had
             * just refreshed successfully. That is the compounding half of the defect Jay hit.
             *
             * `refreshUnderLock` has already re-read the store and retried by the time anything
             * reaches here, so a `SessionExpiredError` means there is nothing in that store for any
             * holder to use.
             */
            endSessionTheServerRefused(refreshError, hadCredential);
            return Promise.reject(refreshError);
          }
        }
        return Promise.reject(error);
      },
    );
  }

  protected async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    if (this.transport) return this.transport.request<T>('GET', url, undefined, config);
    const res = await this.client.get<T>(url, config);
    return res.data;
  }

  protected async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    // F-0236: `/graphql` carries reads as well as writes, so it asserts for itself (`GraphqlClient`,
    // by the query text); a session-establishing POST (sign-in, register, refresh) is not a "write"
    // in AC-11's sense and must still be attempted — refusing it would read as "you're offline" on a
    // door that exists to find that out.
    if (url !== '/graphql' && !establishesSession(url)) assertOnlineForWrite();
    const request = this.transport
      ? this.transport.request<T>('POST', url, data, config)
      : this.client.post<T>(url, data, config).then((res) => res.data);
    // F-0545: an upload in flight holds back the desktop's idle install until it lands.
    return data instanceof FormData ? whileBusy('transfer', request) : request;
  }

  protected async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    assertOnlineForWrite();
    if (this.transport) return this.transport.request<T>('PUT', url, data, config);
    const res = await this.client.put<T>(url, data, config);
    return res.data;
  }

  protected async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    assertOnlineForWrite();
    if (this.transport) return this.transport.request<T>('PATCH', url, data, config);
    const res = await this.client.patch<T>(url, data, config);
    return res.data;
  }

  protected async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    assertOnlineForWrite();
    if (this.transport) return this.transport.request<T>('DELETE', url, undefined, config);
    const res = await this.client.delete<T>(url, config);
    return res.data;
  }

  /**
   * A write that has to outlive the page (UXA-9: an approval held for Undo when the tab closes). On
   * the main connection it is a plain `fetch` with `keepalive`, started before this returns, with the
   * session read up front, as `ViewChoicesApi`'s; it does not renew a lapsed session. A connection's
   * own transport (the desktop relay) does not go with the page, so it is asked as any other POST.
   */
  protected postOutlivingThePage(url: string, data: unknown): Promise<void> {
    if (this.transport) {
      return this.transport.request<unknown>('POST', url, data).then(() => undefined);
    }
    let accessToken: string | null = null;
    try {
      accessToken = requestAccessToken();
    } catch {
      // No readable session: the server refuses it.
    }
    return fetch(`${API_BASE_URL}${url}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify(data),
      keepalive: true,
    }).then(() => undefined);
  }
}
